import { MapViewSvgInteractiveZones } from '@webV2/map/MapView/MapViewSvg/MapViewSvgInteractiveZones';
import React from 'react';
import { useMap } from '@webV2/map/MapProvider';
import { useAuthenticated, useColleagues } from '@gettactic/hooks';
import { IResource } from '@gettactic/api';
import { isTemporarySkipMapClickOrg } from '@/lib/featureFlags';
import { parseInteractiveZonesSvg } from '@gettactic/helpers/src/map/svg/parseInteractiveZonesSvg';

export interface MapInteractiveZonesProps {
  svgRef: React.RefObject<SVGSVGElement | null | undefined>;
  interactionsDisabled?: boolean;
  onReady?: () => void;
}

export const MapInteractiveZones: React.FC<MapInteractiveZonesProps> = ({
  svgRef,
  interactionsDisabled = false,
  onReady
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const {
    schedulable,
    resources,
    startDate,
    selectedResource,
    selectResource,
    events,
    zonesInfo,
    setZonesInfo
  } = useMap();

  const { colleagues } = useColleagues(authenticatedUser?.user?.id || '');
  const temporarySingleMapClick = isTemporarySkipMapClickOrg(authenticatedUser);

  const schedulableObject = React.useMemo(() => {
    return schedulable?.reduce(
      (acc, res) => {
        acc[res.id] = res;
        return acc;
      },
      {} as Record<string, IResource>
    );
  }, [schedulable]);

  const highlightCircleRef = React.useRef<SVGSVGElement | null>(null);
  const focusCircleRef = React.useRef<SVGSVGElement | null>(null);
  const interactiveRef = React.useRef<SVGSVGElement | null>(null);

  const lastAccessedDeskResourceId =
    authenticatedUser?.user?.organization_user
      ?.last_accessed_desk_resource_id ?? null;
  const lastAccessedRoomResourceId =
    authenticatedUser?.user?.organization_user
      ?.last_accessed_meeting_room_resource_id ?? null;

  const handleSelectResource = (resId: string) => {
    if (interactionsDisabled) return;
    selectResource(resId, false, () => {
      setZonesInfo(
        parseInteractiveZonesSvg(svgRef.current!) || {
          mapInfo: null,
          zones: []
        }
      );
    });
  };

  return (
    <MapViewSvgInteractiveZones
      startHidden
      ref={(ref) => {
        interactiveRef.current = ref;
        if (!ref || !svgRef.current) return;
        ref.style.width = svgRef.current.style.width;
        ref.style.height = svgRef.current.style.height;
        ref.style.position = 'absolute';
        ref.style.top = '0';
        ref.style.left = '0';
        setTimeout(() => {
          ref.style.opacity = '1';

          onReady?.();
        }, 50);
      }}
      zonesInfo={zonesInfo}
      resources={schedulable || []}
      totalResources={resources || []}
      resourceSelected={
        !temporarySingleMapClick ? handleSelectResource : undefined
      }
      focusedCircleRef={focusCircleRef}
      highlightCircleRef={highlightCircleRef}
      highlightResource={(resId) => {
        if (interactionsDisabled) return;
        events.trigger(
          'onHighlightResource',
          resId ? schedulableObject?.[resId] || null : null
        );
      }}
      selectedResource={selectedResource}
      lastAccessedDeskResourceId={lastAccessedDeskResourceId}
      lastAccessedRoomResourceId={lastAccessedRoomResourceId}
      authenticatedUser={authenticatedUser}
      startDate={startDate}
      colleagues={colleagues?.elements.map((colleague) => colleague.id) || []}
    />
  );
};
